import { EMPLOYEES_BULK_CREATE_FILE_MAX_LINE } from '@Common/constants/b2b/employees';
import type {
  B2bEmployeeDocumentsListItem,
  B2bEmployeesDocumentsTypeCommon,
  GetB2bEmployeesOneResponse,
  PostCreateB2bEmployeeRequest,
} from '@Common/types/bo/b2b/employees';
import { UserDocumentSource } from '@Common/types/bo/users_documents';

export const b2bEmployeesTranslations = {
  documents: {
    fields: {
      id: 'ID Document',
      employee_id: 'ID Adhérent',
      document_type: 'Type',
      document_url: 'URL',
      document_name: 'Nom',
      source: 'Source',
      is_archived: 'Archivé',
      task_id: 'ID Tâche',
      created_at: 'Créé à',
      updated_at: 'Mis à jour le',
    } satisfies Record<keyof B2bEmployeeDocumentsListItem, string>,
    type: {
      account_statement: 'Relevé de compte',
      trade_confirmation: "Avis d'opération",
      early_withdrawal_request: 'Demande de retrait anticipé',
      w8ben: 'W8BEN',
      annual_statement_csv: 'Relevé annuel (csv)',
      bank_account_statement: 'Relevé bancaire',
      proof_of_address: "Preuve d'adresse",
      account_statement_equities: 'Relevé de compte (actions)',
      rib: 'RIB',
      funding_source: 'Origine des fonds',
      employee_savings_sepa_mandate_form_unsigned: 'Formulaire de mandat SEPA des épargnants',
      employee_savings_sepa_mandate_form_signed: 'Formulaire de mandat SEPA des épargnants signé',
      proof_of_identity: "Pièce d'identité",
      axa_individual_transfer_bulletin: 'Bulletin de transfert individuel AXA',
      axa_taiga_individual_transfer_form: 'Demande de transfert individuel AXA (retraite)',
      axa_voluntary_payment_sof_certificate:
        "Certificat d'origine des fonds AXA (versement volontaire)",
      b2b_account_statement_on_demand: 'Relevé de situation à la demande',
      b2b_transfer_in_statement: "Relevé d'opération transfert entrant",
      b2b_employee_welcome_letter: 'Lettre de bienvenue',
      b2b_ca_shareholder_letter: 'Lettre au porteur (OST)',
      b2b_ca_dividend_statement: 'Relevé de dividendes (OST)',
      b2b_campaign_action_required_letter: "Courrier d'opération collective (action requise)",
      b2b_campaign_confirmation: "Relevé d'opération collective",
      b2b_shareplan_operation_statement: "Relevé d'opération - Augmentation de capital SHAREPLAN",
    } satisfies Record<B2bEmployeesDocumentsTypeCommon, string>,
    sources: {
      app: 'Application',
      bo: 'Backoffice',
      alpaca: 'Alpaca',
      fourthline: 'Fourthline',
      wm_app: 'Wealth Manager Application',
    } satisfies Record<UserDocumentSource, string>,
    empty: "Cet adhérent n'a aucun document pour le moment",
  },
  actions: {
    create: {
      title: 'Créer un adhérent',
      success: 'Adhérent créé',
      failure: "L'adhérent n'a pas pu être créé: %{error}",
      steps: {
        '1': 'Informations personnelles',
        '2': 'Informations professionnelles',
        '3': 'Contact',
      },
      confirm_merge_employee:
        'Un adhérent avec le même NIR et la même date de naissance existe déjà dans cette entreprise. Voulez-vous mettre à jour ses affiliations et ses données ?',
    },
    edit: {
      title: 'Editer un adhérent',
      success_edit_request: "Demande d'édition créée",
      success_auto_edit_request: 'Employé modifié',
      success: 'Adhérent édité',
      failure: "L'adhérent n'a pas pu être édité: %{error}",
      steps: {
        '1': 'Informations personnelles',
        '2': 'Informations professionnelles',
        '3': 'Contact',
        '4': 'Requête',
      },
      validation: {
        email_same_as_secondary_email:
          'L’adresse e-mail principale et l’adresse e-mail secondaire ne peuvent pas être identiques.',
      },
    },
    bulk_create: {
      label: 'Affiliation en masse',
      no_employees_affected: 'Aucun employé affecté par cette importation en masse.',
      success: 'Adhérents mis à jour',
      failure: "L'adhérent n'a pas pu être mis à jour: \n%{error}",
      errors: {
        header_line:
          "Veuillez vérifier les données dans le fichier importé, la ligne d'en-tête doit être placé avant la ligne 3.",
        file_start_line:
          'Veuillez vérifier les données dans le fichier importé, les données doivent commencer à partir de la ligne 3.',
        max_lines_exceeded: `Le fichier contient des données après la ligne ${EMPLOYEES_BULK_CREATE_FILE_MAX_LINE.toLocaleString('fr-FR')}, le maximum autorisé. Veuillez le diviser en plusieurs fichiers.`,
        user_without_mdlr_account:
          "Un des employées importé a déjà un compte utilisateur qui n'a pas de compte mdlr.",
        provide_email_address:
          "Un (ou plusieurs) des employées importé a déjà un compte utilisateur, veuillez s'il vous plaît fournir l'adresse email correspondante dans le fichier.",
      },
      save: 'Uploader le fichier',
      download_template_with_macro: 'Télécharger un fichier template vide ici - avec macro',
      download_template_without_macro: 'Télécharger un fichier template vide ici - sans macro',
      option_title: 'Option',
      automatic_onboarding_description:
        'L’onboarding automatique enverra directement l’invitation et permettra à l’employé de se connecter à son espace, tandis que l’onboarding manuelle nécessitera que vous envoyiez d’abord l’invitation.',
      automatic_onboarding_standard_contracts_note:
        'Les contrats standards sont toujours configurés en onboarding automatique',
      header_1: 'Téléchargez le template',
      header_2: 'Complétez votre fichier',
      header_3: 'Uploadez votre fichier complété',
      description:
        'Ajoutez vos données au fichier en suivant le guide. Exécutez les contrôles pour vérifier que tout est correct.',
      fields: {
        file: 'Fichier',
        company_id: 'Entreprise',
      },
      sections: {
        company_id: 'Entreprise %{companyId}',
        upload: {
          title: 'Effectuez une affiliation en masse',
        },
        overwrites: {
          title: 'Vérifiez les données',
          description:
            "Certaines valeurs de votre fichier vont mettre à jour des données existantes. Veuillez vérifier les champs mis en surbrillance pour vous assurer qu'ils sont corrects.",
          ignored_fields_warning:
            "Certains champs n'ont pas été mis à jour car l'employé les contrôle.",
          mixed_ignored_info:
            "Certains champs n'ont pas été mis à jour car l'employé les contrôle. Seuls les champs contrôlés par l'entreprise seront écrasés.",
          cta: 'Mettre à jour',
          cancel: 'Annuler',
          subtitle: 'Mises à jour',
        },
        results: {
          title: "Résultat de l'import des employés - Identifiant entreprise : %{companyId}",
          subtitle:
            'Votre fichier est en cours de traitement, veuillez patienter pendant la création et la modification des employés.',
          finished: 'Votre fichier a été traité entièrement.',
          no_error: "Pas d'erreur",
          status: {
            pending: 'EN ATTENTE',
            running: 'EN COURS',
            finished: 'TERMINE',
            created: 'Créé',
            updated: 'Modifié',
            error: 'En erreur',
            in_progress: 'En cours',
          },
          line: 'Ligne %{line}',
          actions: {
            created: 'CREATION',
            updated: 'MODIFICATION',
          },
          errors: {
            email_closed_account:
              "Une erreur s'est produite lors de la tentative de mise à jour de l'adresse e-mail. Veuillez vérifier que l'adresse e-mail fournie n'est pas déjà associée à un compte fermé existant.",
            employee_verify_nir_ntt:
              "Une erreur s'est produite lors de la recherche du compte existant, veuillez comparer le NIR ou le NTT fourni dans le fichier donné et la valeur que nous avons dans notre système.",
          },
        },
        validation_errors: {
          title: 'Vérifiez les données',
          description:
            'Certaines valeurs de votre fichier sont dans un format incorrect. Veuillez vérifier le fichier et le corriger avant de procéder au téléchargement.',
          cancel: 'Annuler',
          subtitle: 'Format incorrect',
          info: 'Vous pouvez passer votre souris sur une valeur en surbrillance pour voir les détails.',
          ignored: "L'employé a déjà validé cette donnée.",
          ignored_email:
            "Ces emails ne seront pas mis à jour, car l'employé concerné s'est déjà connecté.",
          required: 'Données requises manquantes.',
          field_incorrect: '(%{field} incorrect)',
          field_not_provided: '(%{field} non fourni)',
          fields: {
            contract_ids: {
              one_required: 'Au moins un identifiant de contrat est requis.',
              missing_taiga_policy_holder_id_for_er:
                'Impossible d’affilier un salarié à un contrat ER sans identifiant Taiga.',
            },
            csp_code: {
              invalid_csp_code:
                "Ce code CSP ne correspond à aucun code configuré dans la règle d'abondement d'un produit du contract.",
            },
            date_of_birth: {
              min_legal_age: 'L´âge de l´employé doit être supérieur à zéro',
            },
            email: {
              database_already_used: "L'adresse e-mail est déjà utilisée dans la base de donnée.",
              file_already_used: "L'adresse e-mail est déjà utilisée dans le fichier.",
            },
            leaving_reason: {
              not_empty_status_old:
                'Le champ « Raison du départ » ne doit pas être vide lorsque le statut est différent de « Présent ».',
              not_empty_work_ending_date_set:
                'La raison de départ ne doit pas être vide lorsque la date de fin de travail est définie.',
              retirement_status_retired:
                'La raison du départ doit être définie sur « Retraite » lorsque le statut est « Retraité ».',
            },
            ntt: {
              file_already_used: 'Le NTT est déjà utilisé dans le fichier.',
            },
            phone_number: {
              database_already_used:
                'Le numéro de téléphone est déjà utilisé dans la base de donnée.',
              file_already_used: 'Le numéro de téléphone est déjà utilisé dans le fichier.',
            },
            registration_number: {
              database_already_used:
                "Le numéro d'enregistrement est déjà utilisé dans cette entreprise dans la base de donnée.",
              file_already_used: "Le numéro d'enregistrement est déjà utilisé dans le fichier.",
            },
            status: {
              current_work_ending_date_future:
                'Le statut doit être défini sur « Présent » lorsque la date de fin du travail est fixée dans le futur.',
              old_work_ending_date_past:
                'Le statut doit être défini sur « Sorti » ou « Retraité » lorsque la date de fin de travail est définie dans le passé.',
              retired_leaving_reason_retirement:
                'Le statut doit être défini sur « Retraité » lorsque la raison du départ est « Retraite ».',
            },
            taiga_policy_holder_id: {
              database_already_used:
                "L'identifiant d'assuré Taiga est déjà utilisé dans la base de donnée.",
              file_already_used: "L'identifiant d'assuré Taiga est déjà utilisé dans le fichier.",
            },
            work_ending_date: {
              later_work_starting_date:
                'La date de fin de travail doit être postérieure à la date de début de travail.',
              not_empty_leaving_reason_set:
                'La date de fin du travail ne doit pas être laissée vide lorsque la raison du départ est définie.',
              not_empty_status_old:
                'La date de fin du travail ne doit pas être vide lorsque le statut est différent de « Présent ».',
            },
            work_starting_date: {
              min_legal_age: 'La date de début de travail doit être supérieure à zéro.',
              work_starting_date_max: 'La date de début de travail ne peut pas être dans le futur.',
            },
          },
        },
      },
    },
    export_multi_company: {
      label: 'Exporter (avec ID société)',
    },
    send_invite: {
      label: "Envoyer l'invitation",
      confirm: "Êtes-vous sûrs de vouloir envoyer une invitation à l'employé ?",
      confirm_paper_mail: "L'employé recevra un courrier papier car aucun e-mail n'a été fourni.",
      success: 'Invitation envoyée',
      failure: "Erreur pendant l'envoie de l'invitation : \n%{error}",
      disabled_company_not_verified:
        "L'invitation ne peut pas être envoyée tant que l'entreprise n'est pas vérifiée",
      disabled_already_activated: "L'employé a déjà été complètement activé",
      disabled_user_closed: 'Le compte utilisateur est fermé',
    },
    bulk_invite: {
      label: 'Envoyer les invitations',
      confirm: 'Êtes-vous sûrs de vouloir envoyer les invitations aux employés ?',
      success: 'Invitations envoyées',
      failure: "Erreur pendant l'envoie des invitations : \n%{error}",
    },
  },
  labels: {
    identity: 'Identité',
    tax_info: 'Informations fiscales',
    nir_helper: 'Ce numéro est visible sur votre carte vitale',
    nir_not_yet: 'NIR manquant',
    nin_not_yet: "Pas de numéro d'identification fiscale",
    address: 'Adresse',
    contact: 'Contact',
    work_info: 'Informations professionnelles',
    bank_info: 'Information bancaire',
    other: 'Autre',
  },
  fields: {
    id: 'ID Adhérent',
    company_id: 'ID Entreprise',
    company_name: 'Nom Entreprise',
    user_id: 'ID utilisateur',
    firstname: 'Prénom',
    lastname: 'Nom',
    name_in_use: "Nom d'usage",
    gender: 'Civilite',
    email: 'E-mail',
    secondary_email: 'Adresse e-mail secondaire pour l’authentification à deux facteurs',
    email_state: 'Statut de l’e-mail',
    secondary_email_state: 'Statut de l’e-mail secondaire',
    phone_number: 'Numéro de téléphone',
    status: 'Situation',
    street: 'Rue',
    postalcode: 'Code postal',
    city: 'Ville',
    country: 'Pays',
    language: 'Langage',
    created_at: 'Créé le',
    updated_at: 'Mis à jour le',
    last_invitation_sent_at: 'Dernière invitation envoyée à',
    date_of_birth: 'Date de naissance',
    place_of_birth: 'Lieu de naissance',
    nationality: 'Nationalité',
    tax_number: "Numéro d'Identification Fiscale",
    work_starting_date: 'Date de début de travail',
    work_ending_date: 'Date de fin de travail',
    investor_profile: 'Profil investisseur PERECOI',
    pei_investor_profile: 'Profil investisseur PEI',
    leaving_reason: 'Raison du départ',
    tax_residence_code: 'Code de résidence fiscale',
    net_employee_deposits: 'Dépôts nets des adhérents',
    net_employer_contributions: 'Cotisations nettes de l’adhérent',
    kyb_status: 'KYB statut',
    retirement_date: 'Date de la retraite',
    social_status: 'Statut social (TS/TNS)',
    role_in_company: 'Rôle dans l’entreprise',
    csp_code: 'Code CSP',
    registration_number: 'Matricule',
    country_of_birth: 'Pays de naissance',
    nir: 'NIR',
    ntt: 'NTT',
    is_migrated: 'Est migré depuis AXA',
    account_status: 'Statut du compte',
    eckert_status: 'Statut Eckert',
    eckert_status_changed_at: 'Date de changement du statut Eckert',
    last_activity_at: 'Date de dernière activité',
    account_closed_at: 'Date de fermeture du compte',
    account_closed_reason: 'Raison de la fermeture du compte',
    approbation_state: "État d'approbation",
    ghost_login: 'Login fantôme',
    is_phone_number_user_controlled: 'Le numéro de téléphone est contrôlé par l’utilisateur',
    is_email_user_controlled: 'Email contrôlé par l’utilisateur',
    is_mailing_address_user_controlled: 'Adresse postale contrôlée par l’utilisateur',
    is_address_user_controlled: 'Adresse contrôlée par l’utilisateur',
    is_retirement_age_user_controlled: 'Âge de la retraite contrôlé par l’utilisateur',
    is_deceased: 'Décédé',
    date_of_decease: 'Date du décès',
    taiga_policy_holder_id: 'Identifiant du titulaire de la police Taiga',
    contract_ids: 'Numéro de contrat',
    contract_types: 'Types de contrat',
    postcode_of_birth: 'Code postal de naissance',
    digital_comms_preferences_id: 'ID des préférences de communication numérique',
    automatic_onboarding: 'Onboarding automatique',
    is_activated_by_company: 'Activé par l’entreprise',
    activation_code_is_used: 'Activé par le salarié',
    personal_details_confirmed:
      'L’employé a confirmé ses coordonnées lors de sa première connexion',
    activation_code: 'Code d’activation',
    has_activation_code: 'Possède un code d’activation',
    external_id_web_code: 'Identifiant web externe',
    activation_code_expires_at: 'Code d’activation expire après',
    activation_code_is_external: 'Code d’activation externe',
    show_activation_code: 'Afficher le code d’activation',
    hide_activation_code: 'Masquer le code d’activation',
    no_activation_code: 'Aucun code d’activation',
    show_external_id_web_code: 'Afficher l’identifiant web externe',
    hide_external_id_web_code: 'Masquer l’identifiant web externe',
    no_external_id_web_code: 'Aucun identifiant web externe',
    has_last_otp: 'Possède un OTP valide',
    last_otp: 'Dernier OTP valide',
    show_last_otp: 'Afficher le dernier OTP valide',
    hide_last_otp: 'Masquer le dernier OTP valide',
    no_last_otp: 'Aucun dernier OTP valide',
    products: 'Couverture Produit',
    is_npai: 'Est NPAI',
    is_npai_mailing: 'Est NPAI Mailing',
    id_external: 'ID External Axa',
    id_external_taiga: 'ID External Taiga',
    is_er_eligible: 'Éligible ER',
    is_campaigns_blocked: 'Est bloqué pour les campagnes',
    is_campaigns_blocked_regulatory: 'Est bloqué pour les campagnes (réglementaire)',
    is_user_closed_or_archived: 'L’utilisateur racine est archivé ou cloturé',
  } satisfies Record<
    | keyof (GetB2bEmployeesOneResponse & PostCreateB2bEmployeeRequest)
    | 'ghost_login'
    | 'activation_code'
    | 'external_id_web_code'
    | 'last_otp'
    | 'show_last_otp'
    | 'no_last_otp'
    | 'hide_last_otp'
    | 'show_activation_code'
    | 'hide_activation_code'
    | 'no_activation_code'
    | 'show_external_id_web_code'
    | 'hide_external_id_web_code'
    | 'no_external_id_web_code',
    string
  >,
  notifications: {
    last_otp_fetch_error: 'Impossible de récupérer le dernier OTP',
    last_otp_unavailable: 'Impossible de récupérer le code, il a expiré ou a déjà été utilisé',
    activation_code_fetch_error: 'Impossible de récupérer le code d’activation',
    activation_code_unavailable: 'Impossible de récupérer le code d’activation',
  },
  portfolio: {
    fields: {
      fund_name: 'Nom du fonds',
    },
    compartments: {
      choose: 'Choisir un compartiment',
      all: 'Tous les compartiments',
      c1_deductible_voluntary_contributions: 'Versements volontaires (Déductibles)',
      c1_non_deductible_voluntary_contributions: 'Versements volontaires (Non déductibles)',
      c2_collective_contributions: 'Épargne salariale',
      c3_mandatory_contributions: 'Cotisations obligatoires',
    },
  },
  choices: {
    status: {
      current: 'Présent',
      past: 'Sorti',
      retired: 'Retraité',
    } satisfies Record<GetB2bEmployeesOneResponse['status'], string>,
    social_status: {
      TS: 'TS',
      TNS: 'TNS',
    } satisfies Record<GetB2bEmployeesOneResponse['social_status'], string>,
    leaving_reason: {
      decease: 'Décès',
      job_transfer: 'Muté',
      retirement: 'Retraite',
      work_ended: 'Fin de contrat de travail',
      other: 'Autre',
    } satisfies Record<Exclude<GetB2bEmployeesOneResponse['leaving_reason'], null>, string>,
    tax_residence_code: {
      tax_resident: 'Résident soumis (RS)',
      non_tax_resident: 'Non Résident (NR)',
      resident_exempt: 'Résident non soumis (RNS)',
    } satisfies Record<Exclude<GetB2bEmployeesOneResponse['tax_residence_code'], null>, string>,
    gender: {
      male: 'Monsieur',
      female: 'Madame',
    },
    language: {
      en: 'Anglais',
      fr: 'Français',
      pl: 'Polonais',
      es: 'Espagnol, Castillan',
      de: 'Allemand',
      it: 'Italien',
      sv: 'Suédois',
      lv: 'Letton',
      lt: 'Lituanien',
      pt: 'Portugais',
      ro: 'Roumain, Moldave',
      sk: 'Slovaque',
      sl: 'Slovène',
      cs: 'Tchèque',
      bg: 'Bulgare',
      hr: 'Croate',
      da: 'Danois',
      fi: 'Finnois',
      el: 'Grec (moderne)',
      hu: 'Hongrois',
    } satisfies Record<Lowercase<Exclude<GetB2bEmployeesOneResponse['language'], null>>, string>,
    account_status: {
      active: 'Actif',
      closed: 'Fermé',
    } satisfies Record<GetB2bEmployeesOneResponse['account_status'], string>,
    eckert_status: {
      not_activated: 'Non activé',
      active: 'Actif',
      inactive: 'Inactif',
      desherence: 'Déshérence',
      attente_depose_cdc: 'En attente de dépôt CDC',
      depose_cdc_pee: 'PEE déposé à la CDC',
      depose_cdc: 'Déposé à la CDC',
    } satisfies Record<GetB2bEmployeesOneResponse['eckert_status'], string>,
    email_state: {
      blocked: 'bloqué',
      permanently_blocked: 'bloqué définitivement',
      invalid: 'invalide',
    } satisfies Record<Exclude<GetB2bEmployeesOneResponse['email_state'], null>, string>,
    email_state_tooltip: {
      blocked:
        'E-mail signalé comme spam ou erreur de distribution. Demandez à l’utilisateur de nous ajouter à ses contacts, puis levez le blocage.',
      permanently_blocked:
        'Cette adresse a de nouveau été rejetée ou signalée comme spam après la levée de son blocage : elle ne peut plus être débloquée. Demandez une autre adresse e-mail à l’utilisateur et mettez-la à jour sur son profil.',
      invalid: 'Livraison impossible. Veuillez vérifier l’adresse ou en demander une nouvelle.',
    } satisfies Record<Exclude<GetB2bEmployeesOneResponse['email_state'], null>, string>,
  },
  validation: {
    csp_code:
      "Le code CSP doit correspondre à l'un d'entre eux : %{cspCodes} (configuré dans les règles d'abondement).",
    tax_number: 'NIF doit faire 13 caractères',
    dob: "L'âge de l'employé doit être supérieur à zéro",
    work_ending_date: 'La date de fin de travail doit être après la date de début de travail',
    work_starting_date: 'La date de début de travail doit être supérieure à zéro.',
    work_starting_date_max: 'La date de début de travail ne peut pas être dans le futur',
    er_requires_taiga_policy_holder_id:
      'Un salarié ne peut pas être affilié à un contrat ER sans identifiant Taiga',
    nir: {
      format_invalid: 'NIR format invalide : doit contenir exactement 13 ou 15 chiffres',
      gender_invalid: 'NIR invalide : le premier chiffre doit être 1 (Homme) ou 2 (Femme)',
      gender_inconsistent:
        'NIR invalide : le premier chiffre doit être 1 (Homme) ou 2 (Femme). Vérifiez la cohérence avec la civilité',
      year_invalid: 'NIR invalide : année de naissance incorrecte',
      year_inconsistent:
        "NIR invalide : incohérent avec l'année de naissance renseignée. Vérifiez le 2ème et 3ème chiffre du NIR",
      month_invalid: 'NIR invalide : mois de naissance incorrect',
      month_inconsistent:
        'NIR invalide : incohérent avec le mois de naissance renseignée. Vérifiez le 4ème et 5ème chiffre du NIR',
      department_invalid: 'NIR invalide : département de naissance incorrect',
      order_number_invalid:
        "NIR invalide : le numéro d'ordre civil ne peut pas être nul. Vérifiez le 11ème, 12ème et 13ème chiffre du NIR",
      control_key_invalid: "la Clé du NIR n'est pas cohérente avec les données renseignées",
      invalid: 'NIR invalide',
      file_already_used: 'Le NIR est déja utilisé dans le fichier',
    },
  },
};
